import i18next from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DashboardWardSelection,
  IDashboardDateRange,
} from "../../../@types/admin";
import { IWardOption } from "../../../@types/User";
import { SkeletonLoading } from "../../../components/Skeleton";
import {
  useActivePatientsPerWard,
  useArchivedPatientsPerWard,
  useDashboardKpis,
  useDeviceUsage,
  usePromResults,
  useReferralSources,
} from "../../../hooks/queries/admin/dashboard";
import { useWards } from "../../../hooks/queries/wards";
import { getShortFormattedDate } from "../../../utils/dateFormatter";
import { isProduction, isUat } from "../../../utils/environment";
import { isForbidden } from "../../../utils/HttpError";
import { getWardLabel } from "../../../utils/wardLabel";
import {
  AccessDeniedBody,
  AccessDeniedCard,
  AccessDeniedTitle,
  ChartBody,
  ChartCard,
  ChartHeader,
  ChartSubtitle,
  ChartTitle,
  Container,
  DashboardGrid,
  HeaderFilters,
  HeaderRow,
  HeaderTextBlock,
  KpiRow,
  ConversationalReportingButton,
  PowerBiButton,
  Subtitle,
  Title,
} from "./AdminHome.styles";
import { ActivePatientsPerWardChart } from "./components/charts/ActivePatientsPerWardChart";
import { ArchivedPatientsPerWardChart } from "./components/charts/ArchivedPatientsPerWardChart";
import { DeviceUsageChart } from "./components/charts/DeviceUsageChart";
import { PromResultsChart } from "./components/charts/PromResultsChart";
import { ReferralSourceChart } from "./components/charts/ReferralSourceChart";
import { DateRangeFilter } from "./components/DateRangeFilter";
import { KpiCard } from "./components/KpiCard";
import { WardFilter } from "./components/WardFilter";

function getInitialDateRange(): IDashboardDateRange {
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 29);
  startDate.setHours(0, 0, 0, 0);
  return { startDate, endDate };
}

function getWardsLabel(
  selection: DashboardWardSelection,
  wards: IWardOption[] | undefined
): string {
  const { singularLower, pluralLower } = getWardLabel();
  if (selection.length === 0)
    return i18next.t("reports_wardsAll", { wards: pluralLower });
  if (selection.length === 1) {
    const ward = wards?.find((w) => w.wardId === selection[0]);
    return (
      ward?.wardName ?? i18next.t("reports_wardsOne", { ward: singularLower })
    );
  }
  return i18next.t("reports_wardsCount", {
    count: selection.length,
    wards: pluralLower,
  });
}

interface IChartCardWrapperProps {
  title: string;
  subtitle: string;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  children: React.ReactNode;
  error?: unknown;
}

function renderChartBody(
  isLoading: boolean,
  isError: boolean,
  isEmpty: boolean,
  children: React.ReactNode,
  error?: unknown
): React.ReactNode {
  if (isLoading) return <SkeletonLoading height={260} />;
  if (isError) {
    if (isForbidden(error)) {
      return (
        <ChartSubtitle>{i18next.t("reports_chart_forbidden")}</ChartSubtitle>
      );
    }
    return <ChartSubtitle>{i18next.t("reports_chart_failed")}</ChartSubtitle>;
  }
  if (isEmpty) {
    return <ChartSubtitle>{i18next.t("reports_chart_noData")}</ChartSubtitle>;
  }
  return children;
}

function ChartCardWrapper({
  title,
  subtitle,
  isLoading,
  isError,
  isEmpty,
  children,
  error,
}: IChartCardWrapperProps) {
  return (
    <ChartCard>
      <ChartHeader>
        <ChartTitle>{title}</ChartTitle>
        <ChartSubtitle>{subtitle}</ChartSubtitle>
      </ChartHeader>
      <ChartBody>
        {renderChartBody(isLoading, isError, isEmpty, children, error)}
      </ChartBody>
    </ChartCard>
  );
}

export function AdminHome() {
  const { t } = useTranslation();

  document.title = t("reports_documentTitle");

  const [wards, setWards] = useState<DashboardWardSelection>([]);
  const [dateRange, setDateRange] =
    useState<IDashboardDateRange>(getInitialDateRange);

  // Same list the WardFilter offers, so the subtitle can name a selected
  // ward the operator isn't rostered on.
  const { wards: allWards } = useWards();
  const { singularLower: wardSingularLower, pluralLower: wardPluralLower } =
    getWardLabel();
  const kpisQuery = useDashboardKpis(wards, dateRange);
  const activeQuery = useActivePatientsPerWard(wards, dateRange);
  const archivedQuery = useArchivedPatientsPerWard(wards, dateRange);
  const devicesQuery = useDeviceUsage(wards, dateRange);
  const referralsQuery = useReferralSources(wards, dateRange);
  const promQuery = usePromResults(wards, dateRange);

  const subtitle = t("reports_subtitle", {
    wards: getWardsLabel(wards, allWards),
    from: getShortFormattedDate(dateRange.startDate.toISOString()),
    to: getShortFormattedDate(dateRange.endDate.toISOString()),
  });

  const { data: kpis, isLoading: kpisLoading } = kpisQuery;

  // If the user lacks the admin-reports:read permission every dashboard endpoint
  // returns 403. Render a single friendly page-level message instead of leaving
  // four KPI cards stuck in their skeleton state and four charts repeating
  // "you don't have permission" — that was the broken UX EN-4961 surfaced.
  const accessDenied = [
    kpisQuery,
    activeQuery,
    archivedQuery,
    devicesQuery,
    referralsQuery,
    promQuery,
  ].some((q) => isForbidden(q.error));

  if (accessDenied) {
    return (
      <Container>
        <HeaderRow>
          <HeaderTextBlock>
            <Title>{t("reports_overview")}</Title>
          </HeaderTextBlock>
        </HeaderRow>
        <AccessDeniedCard>
          <AccessDeniedTitle>
            {t("reports_accessDenied_title")}
          </AccessDeniedTitle>
          <AccessDeniedBody>{t("reports_accessDenied_body")}</AccessDeniedBody>
        </AccessDeniedCard>
      </Container>
    );
  }

  return (
    <Container>
      <HeaderRow>
        <HeaderTextBlock>
          <Title>{t("reports_overview")}</Title>
          <Subtitle>{subtitle}</Subtitle>
        </HeaderTextBlock>
        <HeaderFilters>
          <WardFilter value={wards} onChange={setWards} />
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
          {isUat() && (
            <>
              <ConversationalReportingButton
                href="https://ca-hse-rpm-dashboard.lemonriver-3f60c1f0.westeurope.azurecontainerapps.io/assistant?source=demo-dashboard"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("reports_conversationalReporting")}
              </ConversationalReportingButton>
              <PowerBiButton
                href="https://app.powerbi.com/groups/f6e11a8e-2bff-4afd-bf4a-ec6d31ba7bdc/reports/6c6e8668-3643-43d9-b5ea-69c817ff4e2b/a773d7f4b1243e50e4e8?experience=power-bi"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("reports_powerBi")}
              </PowerBiButton>
            </>
          )}
        </HeaderFilters>
      </HeaderRow>

      <KpiRow>
        <KpiCard
          kpi={kpis?.activePatients}
          accent="primary"
          isLoading={kpisLoading}
          label={t("reports_kpis_activePatients")}
        />
        <KpiCard
          kpi={kpis?.createdPatients}
          accent="secondary"
          isLoading={kpisLoading}
          label={t("reports_kpis_newPatients")}
        />
        <KpiCard
          kpi={kpis?.avgWardDays}
          accent="success"
          isLoading={kpisLoading}
          label={t("reports_kpis_avgTimeOn", { ward: wardSingularLower })}
        />
        <KpiCard
          kpi={kpis?.workflowsTriggered}
          accent="warning"
          isLoading={kpisLoading}
          label={t("reports_kpis_workflowsTriggered")}
        />
      </KpiRow>

      <DashboardGrid>
        <ChartCardWrapper
          title={t("reports_charts_activePerWard", { ward: wardSingularLower })}
          subtitle={t("reports_charts_rankedAcross", {
            wards: wardPluralLower,
          })}
          isLoading={activeQuery.isLoading}
          isError={activeQuery.isError}
          isEmpty={!activeQuery.data || activeQuery.data.length === 0}
          error={activeQuery.error}
        >
          <ActivePatientsPerWardChart data={activeQuery.data ?? []} />
        </ChartCardWrapper>

        <ChartCardWrapper
          title={t("reports_charts_archivedTitle")}
          subtitle={t("reports_charts_rankedAcross", {
            wards: wardPluralLower,
          })}
          isLoading={archivedQuery.isLoading}
          isError={archivedQuery.isError}
          isEmpty={!archivedQuery.data || archivedQuery.data.length === 0}
          error={archivedQuery.error}
        >
          <ArchivedPatientsPerWardChart data={archivedQuery.data ?? []} />
        </ChartCardWrapper>

        <ChartCardWrapper
          title={t("reports_charts_deviceUsageTitle")}
          subtitle={t("reports_charts_deviceUsageSubtitle")}
          isLoading={devicesQuery.isLoading}
          isError={devicesQuery.isError}
          isEmpty={!devicesQuery.data || devicesQuery.data.length === 0}
          error={devicesQuery.error}
        >
          <DeviceUsageChart data={devicesQuery.data ?? []} />
        </ChartCardWrapper>

        <ChartCardWrapper
          title={t("reports_charts_referralTitle")}
          subtitle={t("reports_charts_referralSubtitle")}
          isLoading={referralsQuery.isLoading}
          isError={referralsQuery.isError}
          isEmpty={!referralsQuery.data || referralsQuery.data.length === 0}
          error={referralsQuery.error}
        >
          <ReferralSourceChart data={referralsQuery.data ?? []} />
        </ChartCardWrapper>

        {!isProduction() && (
          <ChartCardWrapper
            title={t("reports_charts_promTitle")}
            subtitle={t("reports_charts_promSubtitle")}
            isLoading={false}
            isError={false}
            isEmpty={false}
          >
            <PromResultsChart />
          </ChartCardWrapper>
        )}
      </DashboardGrid>
    </Container>
  );
}
